import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { fetchUserBrands } from 'slices/users/brands/thunk';
import { UserBrand } from 'types/users';
import '../../../pages/Campaigns/style.scss';
import Loader from 'Components/Common/Loader';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { formatNumber } from 'utils/helpers';
import InstagramLogoIcon from 'assets/icons/InstagramLogoIcon';
import { UncontrolledTooltip } from 'reactstrap';
import {
    getBrandInlineStatusMessage,
    getBrandTooltipMessage,
    getIneligibilityReason,
    sortBrandsByEligibility,
} from 'utils/brandEligibility';

const CampaignCreationBrandSelection: React.FC<{ value: UserBrand | null, onChange: (brand: UserBrand | null) => void, mode: 'prompt' | 'strategy' }> = ({ value, onChange, mode }) => {
    const userBrands = useSelector((state: RootState) => state.userBrands.items);
    const dispatch: AppDispatch = useDispatch();
    const loading = useSelector((state: RootState) => state.userBrands.loading);
    const [selectedBrandId, setSelectedBrandId] = useState<number | null>(value?.id ?? null);

    useEffect(() => {
        dispatch(fetchUserBrands(true));
    }, [])

    useEffect(() => {
        setSelectedBrandId(value?.id ?? null);
    }, [value?.id])

    const brands = useMemo(
        () => sortBrandsByEligibility(userBrands ?? [], mode),
        [userBrands, mode]
    );

    const selectedBrand = useMemo(() => {
        if (!selectedBrandId) return null;
        const brand = brands.find((b) => b.id === selectedBrandId) ?? null;
        if (!brand || getIneligibilityReason(brand, mode)) return null;
        return brand;
    }, [brands, selectedBrandId, mode])

    if (loading) return <Loader />

    return (
        <div className="page-content pt-0 mt-0">
            <div className="new-campaign-creation-mode">
                <div className='new-campaign-creation-mode-header'>
                    <div className="d-flex flex-column justify-content-center align-items-center gap-2">
                        <h1 className="new-campaign-creation-mode-title">
                            Choose the brand you want to build AI-powered campaigns for.
                        </h1>
                        <p className='new-campaign-creation-mode-description'>
                            Select a brand to unlock smart campaign ideas, tailored creatives, and AI-driven growth strategies.
                        </p>
                    </div>
                </div>
                <div className="campaign-brand-picker" aria-label="Brand selection">
                    <div className="campaign-brand-picker__scroll">
                        <PerfectScrollbar options={{ suppressScrollX: true }}>
                            <div className="campaign-brand-picker__list" role="listbox" aria-label="Brands">
                                {brands.length === 0 && (
                                    <div className="campaign-brand-picker__empty">No eligible brands available</div>
                                )}

                                {brands.map((brand) => {
                                    const ineligibilityReason = getIneligibilityReason(brand, mode);
                                    const isEligible = !ineligibilityReason;
                                    const selected = isEligible && selectedBrandId === brand.id;
                                    const sa = brand.social_account;
                                    const handleRaw = sa?.handle ?? '';
                                    const handle = handleRaw ? (handleRaw.startsWith('@') ? handleRaw : `@${handleRaw}`) : '';
                                    const platform = sa?.platform;
                                    const platformIcon = platform === 'ig' ? <InstagramLogoIcon /> : (platform === 'yt' ? <i className='ri-youtube-fill text-danger' /> : null);
                                    const avatarUrl = sa?.profile_picture_url ?? '';
                                    const postsCount = sa?.ig?.media_count ?? 0;
                                    const followersCount = sa?.ig?.followers_count ?? 0;
                                    const followingCount = sa?.ig?.follows_count ?? 0;
                                    const videosCount = sa?.yt?.video_count ?? 0;
                                    const subscribersCount = sa?.yt?.subscriber_count ?? 0;
                                    const viewsCount = sa?.yt?.view_count ?? 0;
                                    const tooltipTargetId = `campaign-brand-option-${brand.id}`;
                                    const tooltipMessage = getBrandTooltipMessage(ineligibilityReason);
                                    const inlineStatusMessage = getBrandInlineStatusMessage(ineligibilityReason);

                                    const optionButton = (
                                        <button
                                            type="button"
                                            className={`campaign-brand-option${selected ? ' campaign-brand-option--selected' : ''}${!isEligible ? ' campaign-brand-option--disabled' : ''} w-100`}
                                            onClick={() => {
                                                if (!isEligible) return;
                                                setSelectedBrandId(brand.id);
                                            }}
                                            disabled={!isEligible}
                                            role="option"
                                            aria-selected={selected}
                                            aria-disabled={!isEligible}
                                        >
                                            <div className="campaign-brand-option__left">
                                                <div className="campaign-brand-option__avatar" aria-hidden="true">
                                                    {avatarUrl ? (
                                                        <img src={avatarUrl} alt="" />
                                                    ) : (
                                                        <span className="campaign-brand-option__avatarFallback">
                                                            {(brand.name?.trim()?.[0] ?? 'B').toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="campaign-brand-option__meta">
                                                    <div className="campaign-brand-option__name">{brand.name}</div>
                                                    {handle && <div className="campaign-brand-option__handle"> {platformIcon} {handle}</div>}
                                                    {inlineStatusMessage && <div className="campaign-brand-option__handle">{inlineStatusMessage}</div>}
                                                </div>
                                            </div>
                                            {
                                                isEligible && (
                                                    <div className="campaign-brand-option__stats" aria-label="Account stats">
                                                        <div className="campaign-brand-option__stat">
                                                            <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(postsCount) : (platform === 'yt' ? formatNumber(videosCount) : '-')}</div>
                                                            <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'No. of posts' : (platform === 'yt' ? 'No. of videos' : '-')}</div>
                                                        </div>
                                                        <div className="campaign-brand-option__stat">
                                                            <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(followersCount) : (platform === 'yt' ? formatNumber(subscribersCount) : '-')}</div>
                                                            <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'Followers' : (platform === 'yt' ? 'Subscribers' : '-')}</div>
                                                        </div>
                                                        <div className="campaign-brand-option__stat">
                                                            <div className="campaign-brand-option__statValue">{platform === 'ig' ? formatNumber(followingCount) : (platform === 'yt' ? formatNumber(viewsCount) : '-')}</div>
                                                            <div className="campaign-brand-option__statLabel">{platform === 'ig' ? 'Following' : (platform === 'yt' ? 'Views' : '-')}</div>
                                                        </div>
                                                    </div>
                                                )}
                                        </button>
                                    );

                                    if (!isEligible && tooltipMessage) {
                                        return (
                                            <React.Fragment key={brand.id}>
                                                <span id={tooltipTargetId} className="campaign-brand-option-wrapper">
                                                    {optionButton}
                                                </span>
                                                <UncontrolledTooltip
                                                    placement="top"
                                                    target={tooltipTargetId}
                                                    innerClassName="custom-tooltip"
                                                >
                                                    {tooltipMessage}
                                                </UncontrolledTooltip>
                                            </React.Fragment>
                                        );
                                    }

                                    return <React.Fragment key={brand.id}>{optionButton}</React.Fragment>;
                                })}
                            </div>
                        </PerfectScrollbar>
                    </div>

                    <div className="campaign-brand-picker__footer">
                        <button
                            type="button"
                            className="btn btn-gradient w-100"
                            disabled={!selectedBrand}
                            onClick={() => onChange(selectedBrand)}
                        >
                            BUILD WITH AI
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CampaignCreationBrandSelection
